/**
 * Dialog for creating a new team with name, slug, and optional settings
 * like timezone, date format, and currency.
 */
import { useState } from "react";

import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { trpc } from "@/lib/trpc";


interface CreateTeamDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onSuccess: () => void;
}

/**
 * Dialog component for creating a new team
 * @param props - Component props
 * @param props.open - Whether the dialog is open
 * @param props.onOpenChange - Callback when dialog open state changes
 * @param props.onSuccess - Callback when team is created successfully
 */
export function CreateTeamDialog({
	open,
	onOpenChange,
	onSuccess,
}: CreateTeamDialogProps) {
	const [name, setName] = useState("");
	const [slug, setSlug] = useState("");
	const [timezone, setTimezone] = useState("");
	const [dateFormat, setDateFormat] = useState("");
	const [currency, setCurrency] = useState("");

	// Create team mutation
	const createMutation = trpc.teams.create.useMutation({
		onSuccess: () => {
			resetForm();
			onSuccess();
		},
	});

	/**
	 * Generates a URL-safe slug from the team name
	 * @param teamName - The team name to convert to a slug
	 */
	const generateSlug = (teamName: string) => {
		return teamName
			.toLowerCase()
			.replace(/[^a-z0-9]+/g, "-")
			.replace(/^-|-$/g, "");
	};

	/**
	 * Handles name input change and auto-generates slug
	 * @param newName - The new team name value
	 */
	const handleNameChange = (newName: string) => {
		setName(newName);
		// Auto-generate slug if slug hasn't been manually edited
		if (!slug || slug === generateSlug(name)) {
			setSlug(generateSlug(newName));
		}
	};

	/** Resets all form fields to initial empty state */
	const resetForm = () => {
		setName("");
		setSlug("");
		setTimezone("");
		setDateFormat("");
		setCurrency("");
	};

	/**
	 * Handles form submission to create the team
	 * @param e - Form submit event
	 */
	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		if (!name.trim() || !slug.trim()) return;

		const settings =
			timezone || dateFormat || currency
				? {
					...(timezone && { timezone }),
					...(dateFormat && { dateFormat }),
					...(currency && { currency }),
				}
				: undefined;

		createMutation.mutate({
			name: name.trim(),
			slug: slug.trim(),
			settings,
		});
	};

	/**
	 * Handles dialog open state change, resets form on close
	 * @param newOpen - New dialog open state
	 */
	const handleOpenChange = (newOpen: boolean) => {
		if (!newOpen) {
			resetForm();
			createMutation.reset();
		}
		onOpenChange(newOpen);
	};

	return (
		<Dialog open={open} onOpenChange={handleOpenChange}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>Create Team</DialogTitle>
					<DialogDescription>
						Create a new team to organize users and manage access.
					</DialogDescription>
				</DialogHeader>
				<form onSubmit={handleSubmit}>
					<div className="space-y-4 py-4">
						<div className="space-y-2">
							<Label htmlFor="name">Team Name</Label>
							<Input
								id="name"
								value={name}
								onChange={(e) => { handleNameChange(e.target.value); }}
								placeholder="e.g., Marketing Team"
							/>
						</div>
						<div className="space-y-2">
							<Label htmlFor="slug">Slug</Label>
							<Input
								id="slug"
								value={slug}
								onChange={(e) => { setSlug(e.target.value); }}
								placeholder="e.g., marketing-team"
								pattern="^[a-z0-9-]+$"
							/>
							<p className="text-xs text-muted-foreground">
								Lowercase letters, numbers, and hyphens only. Must be unique
								within the account.
							</p>
						</div>

						{/* Settings Section */}
						<div className="border-t pt-4 mt-4">
							<p className="text-sm font-medium mb-3">
								Settings{" "}
								<span className="text-muted-foreground font-normal">
									(optional)
								</span>
							</p>
							<div className="space-y-3">
								<div className="space-y-2">
									<Label htmlFor="timezone">Timezone</Label>
									<Input
										id="timezone"
										value={timezone}
										onChange={(e) => { setTimezone(e.target.value); }}
										placeholder="e.g., America/New_York"
									/>
								</div>
								<div className="space-y-2">
									<Label htmlFor="dateFormat">Date Format</Label>
									<Input
										id="dateFormat"
										value={dateFormat}
										onChange={(e) => { setDateFormat(e.target.value); }}
										placeholder="e.g., MM/DD/YYYY"
									/>
								</div>
								<div className="space-y-2">
									<Label htmlFor="currency">Currency</Label>
									<Input
										id="currency"
										value={currency}
										onChange={(e) => { setCurrency(e.target.value); }}
										placeholder="e.g., USD"
									/>
								</div>
							</div>
						</div>

						{/* Error */}
						{createMutation.isError && (
							<div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
								{createMutation.error.message}
							</div>
						)}
					</div>
					<DialogFooter>
						<Button
							type="button"
							variant="outline"
							onClick={() => { handleOpenChange(false); }}
						>
							Cancel
						</Button>
						<Button
							type="submit"
							disabled={
								!name.trim() || !slug.trim() || createMutation.isPending
							}
						>
							{createMutation.isPending ? "Creating..." : "Create Team"}
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
}
